const createRequestGeneration = () => {
    let current = 0;

    return {
        next() {
            current += 1;
            return current;
        },
        current() {
            return current;
        },
        isCurrent(generation) {
            return generation === current;
        },
    };
};

const allowedPeriodsList = (allowedPeriods) => (
    Array.isArray(allowedPeriods) ? allowedPeriods : []
);

const buildMonthOptions = (allowedPeriods, bound) => {
    const months = [];
    const options = [];

    allowedPeriodsList(allowedPeriods).forEach((period) => {
        const month = Number(period?.[bound]);
        if (!Number.isInteger(month) || months.includes(month)) return;

        months.push(month);
        options.push({ value: month, name: period.label });
    });

    return options;
};

const allowedPeriodForMonths = (allowedPeriods, monthFrom, monthTo) => {
    const from = Number(monthFrom);
    const to = Number(monthTo);

    return allowedPeriodsList(allowedPeriods).find((period) => (
        Number(period?.month_from) === from && Number(period?.month_to) === to
    )) || null;
};

const createAbortableRequestSlot = (controllerFactory) => {
    let controller = null;

    const createController = controllerFactory || (() => {
        if (typeof globalThis.AbortController !== 'function') {
            throw new Error('AbortController is unavailable');
        }
        return new globalThis.AbortController();
    });

    return {
        replace() {
            if (controller) controller.abort();
            controller = createController();
            return controller;
        },
        abort() {
            if (controller) controller.abort();
            controller = null;
        },
        get signal() {
            return controller ? controller.signal : null;
        },
    };
};

const paymentContextFor = (contract) => contract?.payment_context || contract?.paymentContext || contract || {};

const contractNumberFor = (contract) => contract?.number || contract?.contract?.number;

const yearFor = (contract) => {
    const context = paymentContextFor(contract);
    const year = context.year?.id ?? context.year;
    return Number.isInteger(year) ? year : null;
};

const allowedServerPeriod = (contract, selectedPeriod) => {
    const context = paymentContextFor(contract);
    const allowedPeriods = context.allowed_periods;
    if (!Array.isArray(allowedPeriods)) return selectedPeriod;

    const matchedPeriod = allowedPeriods.find((period) => period.available !== false && (
        period === selectedPeriod || (
            period.month_from === selectedPeriod?.month_from
            && period.month_to === selectedPeriod?.month_to
        )
    ));
    if (!matchedPeriod) throw new Error('Selected period is not an allowed server period');

    return matchedPeriod;
};

const buildCalculationPayload = (contract, selectedPeriod) => {
    const number = contractNumberFor(contract);
    const year = yearFor(contract);
    const period = allowedServerPeriod(contract, selectedPeriod);

    if (!number || !year || !Number.isInteger(period?.month_from) || !Number.isInteger(period?.month_to)) {
        throw new Error('Public payment data is incomplete');
    }

    return {
        number,
        month_from: period.month_from,
        month_to: period.month_to,
        year,
    };
};

const buildMakePayload = (contract, selectedPeriod, email, requestId) => {
    const normalizedEmail = typeof email === 'string' ? email.trim() : '';
    if (!normalizedEmail || typeof requestId !== 'string' || !requestId) {
        throw new Error('Public payment request is incomplete');
    }

    return {
        ...buildCalculationPayload(contract, selectedPeriod),
        email: normalizedEmail,
        request_id: requestId,
    };
};

const bindValidatedSubmit = (form, onValid) => {
    const preventSubmit = (event) => event.preventDefault();
    const handleValid = () => onValid();

    form.addEventListener('submit', preventSubmit);
    form.addEventListener('bouncerFormValid', handleValid);

    return () => {
        form.removeEventListener('submit', preventSubmit);
        form.removeEventListener('bouncerFormValid', handleValid);
    };
};

const defaultControllerFactory = () => {
    if (typeof globalThis.AbortController === 'function') {
        return new globalThis.AbortController();
    }

    return {
        signal: undefined,
        abort() {},
    };
};

const errorMessage = (error, fallback) => (
    typeof error?.message === 'string' && error.message.trim()
        ? error.message
        : fallback
);

const stateContract = (state) => ({
    contract: state.contract,
    payment_context: state.paymentContext,
});

const clearController = (state, key) => {
    state[key]?.abort();
    state[key] = null;
};

const resetContractState = (state) => {
    const generation = state.requestGeneration.next();
    clearController(state, 'checkController');
    clearController(state, 'calculateController');
    state.payerType = null;
    state.contract = null;
    state.paymentContext = null;
    state.selectedPeriod = null;
    state.paymentSum = null;
    state.publicRequestId = null;
    state.formError = '';
    state.formSending = false;
    state.calculationPending = false;
    state.makePending = false;
    state.makePromise = null;
    state.formPay.mouth_from = '';
    state.formPay.mouth_to = '';

    return generation;
};

const resetPeriodState = (state, selectedPeriod = null) => {
    const generation = state.requestGeneration.next();
    clearController(state, 'calculateController');
    state.selectedPeriod = selectedPeriod;
    state.paymentSum = null;
    state.publicRequestId = null;
    state.formError = '';
    state.calculationPending = false;

    return generation;
};

const checkData = (response) => {
    const data = response?.data;
    if (
        !data
        || !['applicant', 'student'].includes(data.payer_type)
        || !data.contract
        || !data.payment_context
    ) {
        throw new Error('Не удалось обработать данные договора');
    }

    return data;
};

const createPublicPaymentFlow = (state, dependencies) => {
    const {
        loadCheck,
        loadCalculation,
        loadMake,
        createRequestId,
        redirect,
        controllerFactory = defaultControllerFactory,
    } = dependencies;

    const isCurrent = (generation) => state.requestGeneration.isCurrent(generation);

    const agreementChanged = () => {
        resetContractState(state);
        state.step = 1;
    };

    const checkAgreement = () => {
        const generation = resetContractState(state);
        const agreement = state.formData.agreement;
        const controller = controllerFactory();
        state.checkController = controller;
        state.formSending = true;

        return (async () => {
            try {
                const response = await loadCheck(agreement, controller.signal);
                if (!isCurrent(generation)) return;

                const data = checkData(response);
                state.payerType = data.payer_type;
                state.contract = data.contract;
                state.paymentContext = data.payment_context;

                if (!isCurrent(generation)) return;
                state.step = 2;
                state.formSending = false;
                if (state.checkController === controller) state.checkController = null;

                if (data.payment_context.mode === 'semester') {
                    const semester = (data.payment_context.allowed_periods || [])
                        .find((period) => period?.available !== false);
                    if (!semester) throw new Error('Не удалось получить доступный период');
                    await selectPeriod(semester);
                } else if (data.payment_context.mode === 'months') {
                    const monthFrom = Number(data.payment_context.month_from);
                    const defaultPeriod = (data.payment_context.allowed_periods || [])
                        .filter((period) => (
                            period?.available !== false && Number(period.month_from) === monthFrom
                        ))
                        .sort((a, b) => Number(a.month_to) - Number(b.month_to))[0] || null;
                    if (!defaultPeriod) throw new Error('Не удалось получить доступный период');
                    await selectPeriod(defaultPeriod);
                }
            } catch (error) {
                if (!isCurrent(generation)) return;
                state.formError = errorMessage(error, 'Произошла ошибка при проверке договора');
            } finally {
                if (!isCurrent(generation)) return;
                state.formSending = false;
                if (state.checkController === controller) state.checkController = null;
            }
        })();
    };

    const refreshAfterStale = async () => {
        resetContractState(state);
        await checkAgreement();
    };

    const calculate = () => {
        if (!state.selectedPeriod) return Promise.resolve();

        const generation = state.requestGeneration.next();
        clearController(state, 'calculateController');
        const controller = controllerFactory();
        state.calculateController = controller;
        state.paymentSum = null;
        state.formError = '';
        state.calculationPending = true;

        return (async () => {
            try {
                const payload = buildCalculationPayload(
                    stateContract(state),
                    state.selectedPeriod
                );
                const response = await loadCalculation(payload, controller.signal);
                if (!isCurrent(generation)) return;
                state.paymentSum = response;
            } catch (error) {
                if (!isCurrent(generation)) return;
                if (error?.code === 'PAYMENT_CONTEXT_STALE') {
                    await refreshAfterStale();
                    return;
                }
                state.formError = errorMessage(error, 'Произошла ошибка при расчёте платежа');
            } finally {
                if (!isCurrent(generation)) return;
                state.calculationPending = false;
                if (state.calculateController === controller) state.calculateController = null;
            }
        })();
    };

    const selectPeriod = (period) => {
        resetPeriodState(state, period);
        return calculate();
    };

    const selectApplicantPeriod = (monthFrom, monthTo) => {
        const allowedMonths = Array.isArray(state.paymentContext?.allowed_months)
            ? state.paymentContext.allowed_months
            : [];
        const from = Number(monthFrom);
        const to = Number(monthTo || monthFrom);
        const allowedValues = allowedMonths.map(month => Number(month.value));

        if (!allowedValues.includes(from) || !allowedValues.includes(to) || to < from) {
            resetPeriodState(state);
            return Promise.resolve();
        }

        return selectPeriod({ month_from: from, month_to: to });
    };

    const make = () => {
        if (state.makePending && state.makePromise) return state.makePromise;

        const generation = state.requestGeneration.next();
        const normalizedEmail = typeof state.formPay.email === 'string'
            ? state.formPay.email.trim()
            : '';
        if (!normalizedEmail || !state.paymentSum || !state.selectedPeriod) {
            state.formError = 'Заполните все обязательные поля';
            return Promise.resolve();
        }

        if (!state.publicRequestId) state.publicRequestId = createRequestId();
        const payload = buildMakePayload(
            stateContract(state),
            state.selectedPeriod,
            normalizedEmail,
            state.publicRequestId
        );
        state.formError = '';
        state.makePending = true;

        let makePromise;
        makePromise = (async () => {
            try {
                const response = await loadMake(payload);
                if (!isCurrent(generation)) return;

                const redirectUrl = response?.data?.confirmation?.confirmation_url;
                if (typeof redirectUrl !== 'string' || !redirectUrl) {
                    throw new Error('Не удалось получить ссылку на оплату');
                }

                state.publicRequestId = null;
                redirect(redirectUrl);
            } catch (error) {
                if (!isCurrent(generation)) return;
                if (error?.code === 'PAYMENT_CONTEXT_STALE') {
                    await refreshAfterStale();
                    return;
                }
                state.formError = errorMessage(error, 'Произошла ошибка при оплате');
            } finally {
                if (!isCurrent(generation)) return;
                state.makePending = false;
                if (state.makePromise === makePromise) state.makePromise = null;
            }
        })();

        state.makePromise = makePromise;
        return makePromise;
    };

    const dispose = () => {
        state.requestGeneration.next();
        clearController(state, 'checkController');
        clearController(state, 'calculateController');
    };

    return {
        agreementChanged,
        checkAgreement,
        selectPeriod,
        selectApplicantPeriod,
        calculate,
        make,
        dispose,
    };
};

module.exports = {
    createRequestGeneration,
    buildMonthOptions,
    allowedPeriodForMonths,
    createAbortableRequestSlot,
    buildCalculationPayload,
    buildMakePayload,
    bindValidatedSubmit,
    resetContractState,
    resetPeriodState,
    createPublicPaymentFlow,
};
